import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { VALUES, COMPANY, QUOTES } from "../data";

const ease = [0.16, 1, 0.3, 1];

export function About() {
  return (
    <div data-testid="about-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">
          About — Est. {COMPANY.foundedYear}
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl text-balance text-[#0F1E3C]"
        >
          We build teams that
          <br />
          <span className="italic font-serif gradient-text-amber">outlast quarters.</span>
        </motion.h1>

        <p className="mt-6 text-sm text-[#64748B] max-w-3xl">
          Albany Staffing is a <strong className="text-[#0F1E3C]">DBA (Doing Business As) of Albany Systems LLC</strong>,
          proudly headquartered in Lisle, Illinois.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7 text-xl sm:text-2xl text-[#0F1E3C] leading-relaxed font-light"
          >
            Albany Staffing is a premier staffing &amp; recruitment firm built on a simple
            belief: the right hire changes a company&rsquo;s trajectory. For more than a
            decade, we&rsquo;ve helped US organizations — from Fortune 500 industrials to
            high-growth SaaS — find senior talent across IT and non-IT disciplines.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="lg:col-span-5 lg:col-start-8 text-base text-[#475569] leading-relaxed"
          >
            <p>
              Headquartered in Lisle, Illinois, with a nationwide sourcing network,
              we specialize in direct hire staffing, contract placements, executive
              search, and technical recruitment — with deep benches in Manufacturing,
              Aerospace, Defense, Healthcare, Life Sciences, and IT.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-white border-y border-[#EAE3D2] py-20 sm:py-24">
        <div className="container-x max-w-5xl text-center">
          <Quote className="w-10 h-10 text-[#E9610E] mx-auto mb-5 opacity-70" strokeWidth={1.5} />
          <blockquote className="font-serif italic text-2xl sm:text-4xl lg:text-5xl text-[#0F1E3C] leading-[1.2] tracking-tight text-balance">
            &ldquo;{QUOTES[1].text}&rdquo;
          </blockquote>
        </div>
      </section>

      {/* Values */}
      <section className="section-y container-x">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">
          What we stand for
        </p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F1E3C] mb-12 max-w-3xl">
          Four principles. Zero compromises.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease }}
              className="card-soft rounded-2xl p-8 sm:p-10 transition-all duration-300"
              data-testid={`value-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="w-9 h-9 rounded-full bg-[#FFF4EA] text-[#E9610E] flex items-center justify-center font-bold text-sm">
                  0{i + 1}
                </span>
                <h3 className="font-display font-bold text-2xl tracking-tight text-[#0F1E3C]">{v.title}</h3>
              </div>
              <p className="text-base text-[#475569] leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HQ */}
      <section className="section-y container-x border-t border-[#EAE3D2]">
        <div className="pt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-3">Our footprint</p>
            <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F1E3C] mb-4">
              Rooted in Illinois. Working across America.
            </h3>
            <p className="text-base text-[#475569] leading-relaxed">
              Our Lisle headquarters sits in the heart of the Chicago technology corridor,
              with recruiters and advisors spanning every major US time zone — so whichever
              coast your hiring manager is on, we&rsquo;re already awake and on it.
            </p>
          </div>
          <div className="lg:col-span-7 card-soft rounded-2xl p-10">
            <div className="text-xs uppercase tracking-[0.15em] text-[#E9610E] font-semibold mb-2">Headquarters</div>
            <div className="font-display font-bold text-2xl text-[#0F1E3C] leading-tight">{COMPANY.address}</div>
            <div className="mt-4 text-sm text-[#475569]">{COMPANY.email}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
